import {
  validateLayoutStructure,
  validKeycapsByTypeKey,
  hasOwnField,
} from './layoutValidation';

const normalizeComparableKeycap = (keycap) => ({
  width: keycap.width ?? null,
  height: keycap.height ?? null,
  isConvex: !!keycap.isConvex,
  isOffCentered: !!keycap.isOffCentered,
  isIsoEnter: !!keycap.isIsoEnter,
  hasHomingBar: !!keycap.hasHomingBar,
  stepped: !!keycap.stepped,
}); // keeps only the keycap fields that matter when comparing the actual layout arrangement

const isComparableKeycap = (keycap) =>
  keycap && Object.keys(keycap).length > 0 && !keycap.isSpacer; // only keep real keycap objects and ignore spacers or empty objects

const normalizeLayoutArrangement = (layout) =>
  layout
    .map((row) =>
      row
        .filter((keycap) => isComparableKeycap(keycap)) // remove spacers and empty objects before comparing rows
        .map((keycap) => normalizeComparableKeycap(keycap))
    )
    .filter((row) => row.length > 0); // remove empty rows because they do not affect the actual keycap arrangement

const comparableBooleanKeycapFields = [
  'isConvex',
  'isOffCentered',
  'isIsoEnter',
  'hasHomingBar',
  'stepped',
]; // boolean fields where missing and false should be treated the same

const keycapsHaveSameComparableFields = (keycapA, keycapB) =>
  keycapA.width === keycapB.width &&
  keycapA.height === keycapB.height &&
  comparableBooleanKeycapFields.every(
    (field) => !!keycapA[field] === !!keycapB[field]
  ); // checks whether two keycaps have the same physical fields, treating missing boolean fields as false

export const layoutsHaveSameArrangement = (layoutA, layoutB) => {
  const normalizedLayoutA = normalizeLayoutArrangement(layoutA); // remove spacers and normalize keycap fields for the first layout
  const normalizedLayoutB = normalizeLayoutArrangement(layoutB); // remove spacers and normalize keycap fields for the second layout

  if (normalizedLayoutA.length !== normalizedLayoutB.length) return false; // layouts must have the same number of rows

  return normalizedLayoutA.every((rowA, rowIndex) => {
    // go through every row in the first layout:
    const rowB = normalizedLayoutB[rowIndex];

    if (rowA.length !== rowB.length) return false; // rows must have the same number of keycaps after spacers are removed

    return rowA.every((keycapA, keycapIndex) =>
      keycapsHaveSameComparableFields(keycapA, rowB[keycapIndex])
    ); // keycaps must appear in the same order and have the same comparable fields
  });
}; // checks whether two layouts have the same rows and keycap order after spacers are removed

const overlapToleranceInMM = 0.01; // allow tiny floating-point differences when keycaps only touch edges

const transformSvgPoint = (svg, matrix, x, y) => {
  const point = svg.createSVGPoint(); // create an SVG point object (an empty/default SVG coordinate point, where point.x and point.y are usually 0). This SVG point is needed, so that we can use .matrixTransform().

  point.x = x; // set the corner's original/local x position
  point.y = y; // set the corner's original/local y position

  return point.matrixTransform(matrix); // apply the element's full SVG transform so the point matches where it is actually rendered
}; // converts one local footprint corner into its final rendered SVG position

const getTransformedElementBoxPolygon = (svg, element) => {
  const box = element.getBBox(); // get the local bounding box of the rendered keycap footprint
  const matrix = element.getCTM(); // get the full SVG transform (including parent groups, rotations and translations) that moves this footprint from local coordinates into its rendered position

  if (!matrix) return null; // skip elements where the browser cannot provide a transform matrix

  return [
    transformSvgPoint(svg, matrix, box.x, box.y),
    transformSvgPoint(svg, matrix, box.x + box.width, box.y),
    transformSvgPoint(svg, matrix, box.x + box.width, box.y + box.height),
    transformSvgPoint(svg, matrix, box.x, box.y + box.height),
  ]; // return the polygon for this keycap footprint (which is the coordinates of the four corners of the rendered footprint)
}; // converts a svg element into a polygon

const normalizeAxis = (axis) => {
  const length = Math.sqrt(axis.x * axis.x + axis.y * axis.y); // calculate how long this axis vector is (using the Pythagorean theorem: sqrt(x² + y²))

  if (length === 0) {
    // if the axis has no length, it cannot be used for projection:
    return {
      x: 0,
      y: 0,
    }; // return a safe empty axis to avoid dividing by zero
  }

  return {
    x: axis.x / length,
    y: axis.y / length,
  }; // shrink or stretch the axis to length 1 while keeping the same direction
}; // normalizes an axis so projection values stay in real SVG units (in my case mm) and can be compared with the SVG-unit tolerance (overlapToleranceInMM). By making the axis being of length 1, following projections don't depend on the axis length anymore.

const getPolygonAxes = (polygon) =>
  polygon.map((point, index) => {
    const nextPoint = polygon[(index + 1) % polygon.length]; // get the next corner/point ('% polygon.length' makes sure that for the last corner (index 3) the next corner is not at index 4 (which does not exist), but rather points to index 0 again (so it wraps back to the first corner/point))

    const edge = {
      x: nextPoint.x - point.x,
      y: nextPoint.y - point.y,
    }; // create a vector that points along the current polygon edge

    return normalizeAxis({
      x: -edge.y,
      y: edge.x,
    }); // create a perpendicular axis for this edge, because this is where two polygons can be separated
  }); // gets all axes that need to be tested for this polygon

const getProjectionRange = (polygon, axis) => {
  const projections = polygon.map(
    (point) => point.x * axis.x + point.y * axis.y
  ); // convert each polygon corner into a single number that shows where it lands on this axis

  return {
    min: Math.min(...projections),
    max: Math.max(...projections),
  }; // return the first and last position of the polygon on this axis
}; // flattens a polygon onto one axis so two polygons can be compared in one dimension

const rangesOverlap = (rangeA, rangeB) => {
  const rangeAReachesIntoRangeB =
    rangeA.max > rangeB.min + overlapToleranceInMM; // rangeA must pass the start of rangeB by more than the tolerance

  const rangeBReachesIntoRangeA =
    rangeB.max > rangeA.min + overlapToleranceInMM; // rangeB must pass the start of rangeA by more than the tolerance

  return rangeAReachesIntoRangeB && rangeBReachesIntoRangeA; // both checks must be true for the ranges to overlap
}; // checks whether two one-dimensional projection ranges overlap

export const polygonsOverlap = (polygonA, polygonB) => {
  const axes = [...getPolygonAxes(polygonA), ...getPolygonAxes(polygonB)]; // collect the test axes from both polygons, because either shape can provide the separating direction

  return axes.every((axis) => {
    // test every possible separating axis:
    const rangeA = getProjectionRange(polygonA, axis); // flatten the first polygon onto the current axis
    const rangeB = getProjectionRange(polygonB, axis); // flatten the second polygon onto the current axis

    return rangesOverlap(rangeA, rangeB); // if the flattened ranges overlap on this axis, keep checking the next axis
  });
}; // returns true only when the polygons overlap on every axis, meaning there is no gap between them (Separating Axis Theorem)

export const findOverlappingRenderedKeycaps = (svgElement) => {
  if (!svgElement) return [];

  const footprintElements = [
    ...svgElement.querySelectorAll('[data-keycap-footprint="true"]'),
  ]; // get all rendered keycap footprint shapes from the layout svg

  // convert all keycap footprints into polygons and store them inside the footprints array:
  const footprints = footprintElements
    .map((element) => {
      const polygon = getTransformedElementBoxPolygon(svgElement, element); // convert the current keycap footprint into a polygon, which is a closed shape described by its corner coordinates

      if (!polygon) return null; // skip footprint shapes where no transformed polygon could be created

      return {
        rowIndex: Number(element.dataset.rowIndex),
        keycapIndex: Number(element.dataset.keycapIndex),
        label: element.dataset.keycapLabel,
        polygon,
      }; // add the current polygon to the footprints array
    })
    .filter(Boolean); // remove skipped footprint shapes

  const overlaps = []; // stores all overlapping keycap pairs
  const overlapKeys = new Set(); // avoids reporting the same keycap pair multiple times (which can happen for the ISO-Enter footprint for example, as it is made out of two elements/rects)

  // compare all possible footprint pairs with each other:
  for (let i = 0; i < footprints.length; i += 1) {
    // go through all keycap footprints:
    for (let j = i + 1; j < footprints.length; j += 1) {
      // compare the current footprint with every following footprint:
      const sameKeycap =
        footprints[i].rowIndex === footprints[j].rowIndex &&
        footprints[i].keycapIndex === footprints[j].keycapIndex; // check if both footprint parts belong to the same keycap (for example the ISO-Enter footprint is made out of two rects)

      if (sameKeycap) continue; // skip footprint parts that belong to the same keycap footprint

      if (polygonsOverlap(footprints[i].polygon, footprints[j].polygon)) {
        // if the current pair of keycap footprints overlap (polygonsOverlap makes use of the Separating Axis Theorem):
        const overlapKey = [
          footprints[i].rowIndex,
          footprints[i].keycapIndex,
          footprints[j].rowIndex,
          footprints[j].keycapIndex,
        ].join('-'); // create a stable key for the current keycap pair

        if (overlapKeys.has(overlapKey)) continue; // skip duplicate reports

        overlapKeys.add(overlapKey);

        overlaps.push({
          first: footprints[i],
          second: footprints[j],
        }); // store both keycaps so the UI can show a useful error
      }
    }
  }

  return overlaps;
}; // finds overlaps based on the actually rendered svg footprint shapes

const addLayoutWarning = (warnings, message) => {
  warnings.push(message); // stores one non-blocking layout warning
}; // adds one layout warning

const getHomingBarWarnings = (layout) => {
  const warnings = []; // stores all homing bar related warnings

  let homingBarCount = 0; // counts all 1u homing bar keycaps
  let totalKeycapCount = 0; // counts all real keycaps
  let hasNumpad = false; // tracks whether the layout contains numpad keys

  layout.forEach((row) => {
    row.forEach((keycap) => {
      if (keycap.isSpacer) {
        return; // spacers are ignored
      }

      totalKeycapCount += 1; // count every real keycap

      const HOMING_BAR_XDA_KEYCAP_ID =
        validKeycapsByTypeKey['1x1-homing-bar'].xdaKeycapId;

      if (keycap.profiles?.XDA === HOMING_BAR_XDA_KEYCAP_ID) {
        homingBarCount += 1; // count 1u homing bar keycaps
      }

      if (keycap.group === 'numpad') {
        hasNumpad = true; // remember that this layout contains a numpad
      }
    });
  });

  const shouldHaveThreeHomingBars = hasNumpad || totalKeycapCount > 90; // larger layouts usually need three homing bars

  const requiredHomingBars = shouldHaveThreeHomingBars ? 3 : 2;

  if (homingBarCount < requiredHomingBars) {
    addLayoutWarning(
      warnings,
      shouldHaveThreeHomingBars
        ? `Das Layout enthält nur ${homingBarCount} 1u-Homing-Bar-Keycaps. Bei Layouts mit Numpad oder mehr als 90 Keycaps werden normalerweise 3 Stück erwartet. Ist das beabsichtigt?`
        : `Das Layout enthält nur ${homingBarCount} 1u-Homing-Bar-Keycaps. Normalerweise werden mindestens 2 Stück erwartet. Ist das beabsichtigt?`
    );
  }

  return warnings;
}; // checks whether the layout contains enough homing bar keycaps

const getMissingKeycapIdWarnings = (layout) => {
  const warnings = []; // stores all missing keycap id warnings
  const missingKeycapIds = []; // stores the locations of keycaps without a keycapId field

  layout.forEach((row, rowIndex) => {
    row.forEach((keycap, keycapIndex) => {
      if (keycap.isSpacer) {
        return; // spacers do not have keycap ids
      }

      if (!hasOwnField(keycap, 'keycapId')) {
        missingKeycapIds.push(`layout[${rowIndex}][${keycapIndex}]`);
      }
    });
  });

  if (missingKeycapIds.length > 0) {
    addLayoutWarning(
      warnings,
      `Folgende Keycaps haben keine keycapId:\n- ${missingKeycapIds.join(
        '\n- '
      )}`
    );
  }

  return warnings;
}; // checks whether all real keycaps contain a keycapId field

const validateLayoutWarnings = (layout) => {
  const warnings = [];

  warnings.push(...getHomingBarWarnings(layout));
  warnings.push(...getMissingKeycapIdWarnings(layout));

  return warnings;
}; // validates all non-blocking layout warnings

export const checkLayout = (layout) => {
  const problems = validateLayoutStructure(layout); // validate the parsed layout structure and all objects

  return {
    problems,
    warnings: problems.length > 0 ? [] : validateLayoutWarnings(layout), // the warnings read the layout as rows of keycap objects, which only a layout without problems guarantees
  };
}; // checks the parsed layout: the validator's problems, and the non-blocking warnings once it has none

export const findDuplicateLayouts = (
  validLayout,
  existingLayouts,
  { layoutId, numOfKeycaps }
) =>
  existingLayouts.filter((existingLayout) => {
    // go through all existing layout documents:
    if (String(existingLayout._id) === String(layoutId)) return false; // skip the layout currently being edited (because it would otherwise match itself)

    if (Number(existingLayout.numOfKeycaps) !== Number(numOfKeycaps)) {
      return false; // only compare the full layout arrangement if both layouts have the same number of keycaps
    }

    return layoutsHaveSameArrangement(validLayout, existingLayout.layout); // check whether the keycaps are arranged in the same rows and order, while ignoring spacers
  }); // the saved layouts the edited layout duplicates: the same key count and the same arrangement, the edited layout itself skipped (LayoutEditScreen, PR 32)
